import { apiBaseUrl, toError, ApiError } from './client';
import { keysToCamel } from './caseConvert';
import type { ProfileHalf, ScriptHalf } from './profileReading';

const IDLE_MS = 40_000;

export type ReadingFrame =
  | ({ frame: 'profile' } & ProfileHalf)
  | ({ frame: 'script' } & ScriptHalf)
  | { frame: 'error'; code: string; message: string };

const KNOWN_FRAMES = new Set(['profile', 'script', 'error']);

/** Guards the cast below against a frame kind this build has never heard of. */
function isReadingFrame(value: unknown): value is ReadingFrame {
  return (
    typeof value === 'object' &&
    value !== null &&
    'frame' in value &&
    typeof (value as { frame: unknown }).frame === 'string' &&
    KNOWN_FRAMES.has((value as { frame: string }).frame)
  );
}

type ReadResult = ReadableStreamReadResult<Uint8Array>;

/** Resolves to `'idle'` when `idleMs` passes before `read` settles. */
function withIdleLimit(read: Promise<ReadResult>, idleMs: number): Promise<ReadResult | 'idle'> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve('idle'), idleMs);
    read.then(
      (result) => {
        clearTimeout(timer);
        resolve(result);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Frames arrive whole or not at all. The buffer holds the tail of a chunk
 * until its newline shows up, because a `ReadableStream` splits wherever it
 * likes and this body is around 206 KB.
 */
export async function* readProfileFrames(
  handle: string,
  options: { signal?: AbortSignal; idleMs?: number } = {}
): AsyncGenerator<ReadingFrame> {
  const response = await fetch(`${apiBaseUrl()}/v1/profile-reading`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ instagram_handle: handle }),
    signal: options.signal
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw toError(response.status, payload);
  }
  if (!response.body) {
    throw new ApiError(
      'Não conseguimos completar essa ação. Tente de novo.',
      response.status,
      'no_response_body'
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const next = await withIdleLimit(reader.read(), options.idleMs ?? IDLE_MS);
    if (next === 'idle') {
      yield {
        frame: 'error',
        code: 'reading_interrupted',
        message: 'A conexão caiu antes do roteiro chegar.'
      };
      await reader.cancel().catch(() => undefined);
      return;
    }
    if (next.done) return;

    buffer += decoder.decode(next.value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline !== -1) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line !== '') {
        // The wire is snake_case like every other route; this is the same
        // conversion `apiRequest` runs on its response.
        const parsed = keysToCamel<unknown>(JSON.parse(line));
        // A frame kind this build has never heard of is not this stream's
        // failure: it may be a future server sending something ahead of the
        // `script` frame still on its way. Skip it and keep reading.
        if (isReadingFrame(parsed)) {
          yield parsed;
        }
      }
      newline = buffer.indexOf('\n');
    }
  }
}
